import type React from 'react';
import type { VideoAsset } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import { LIBRARY_VIDEO_DRAG_MIME } from './blockPaletteConstants';

function makeDragStartHandler(video: VideoAsset, onDragStart: () => void) {
  return (event: React.DragEvent) => {
    event.dataTransfer.setData(LIBRARY_VIDEO_DRAG_MIME, video.externalId);
    event.dataTransfer.effectAllowed = 'copy';
    onDragStart();
  };
}

function makeKeyDownHandler(onPick: () => void) {
  return (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onPick();
    }
  };
}

/**
 * Drag-to-slide and click/keyboard-pick props for a `VideoLibraryCard` — both are optional
 * and independent (a card can be pick-only, drag-only, both, or neither, e.g. read-only).
 */
export function useVideoLibraryCardInteraction({
  video,
  onPick,
  onDragStart,
  onDragEnd,
}: {
  video: VideoAsset;
  onPick?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const cursor = onPick ? 'pointer' : onDragStart ? 'grab' : 'default';
  return {
    draggable: onDragStart ? true : undefined,
    onDragStart: onDragStart ? makeDragStartHandler(video, onDragStart) : undefined,
    onDragEnd,
    role: onPick ? ('button' as const) : undefined,
    ariaLabel: onPick ? `Select ${video.title}` : undefined,
    tabIndex: onPick ? 0 : undefined,
    onClick: onPick,
    onKeyDown: onPick ? makeKeyDownHandler(onPick) : undefined,
    cursor,
  };
}
